import React from "react";
import { Helmet } from "react-helmet-async";
import {
  FaShower,
  FaTrashAlt,
  FaHeartbeat,
  FaTint,
  FaRegSadTear,
  FaMedkit,
  FaHeart,
  FaExclamationTriangle,
  FaHome,
} from "react-icons/fa";
import { FiHome } from "react-icons/fi";
import { GiUnderwearShorts } from "react-icons/gi";
import { MdHealthAndSafety, MdEmojiPeople } from "react-icons/md";
import { Link } from "react-router-dom";

const SafetyAwareness = () => {
  const safetyData = [
    {
      id: "hygiene",
      title: "Hygiene Practices",
      icon: <FaShower className="text-[#9ea3f5] text-2xl" />,
      color: "bg-gradient-to-r from-[#9ea3f5] to-[#ccdcfd]",
      tips: [
        {
          title: "Change Products Regularly",
          content:
            "Replace sanitary products every 4–6 hours to prevent infections and odor.",
        },
        {
          title: "Proper Clothing",
          content:
            "Use clean, dry underwear and avoid tight synthetic fabrics that can cause irritation.",
        },
        {
          title: "Gentle Cleaning",
          content:
            "Wash the genital area with lukewarm water only. Avoid scented soaps or vaginal washes that can disrupt pH balance.",
        },
      ],
      additional: (
        <div className="mt-6 bg-[#f0e9ff] p-4 rounded-lg border border-[#d9cdfa]">
          <h3 className="font-bold text-[#6e5ba8] mb-2 flex items-center">
            <GiUnderwearShorts className="text-[#9ea3f5] mr-2" />
            Clothing Tips
          </h3>
          <ul className="list-disc pl-5 text-[#5a4d8a] space-y-1">
            <li>Choose cotton underwear for better breathability</li>
            <li>Dark colors can help prevent visible stains</li>
            <li>Keep an extra pair in your bag for emergencies</li>
          </ul>
        </div>
      ),
    },
    {
      id: "products",
      title: "Menstrual Products",
      icon: <FaTint className="text-[#e8bffb] text-2xl" />,
      color: "bg-gradient-to-r from-[#e8bffb] to-[#d9cdfa]",
      tips: [
        {
          title: "Sanitary Pads",
          content:
            "Easy to use, especially for beginners. Change every 4-6 hours.",
        },
        {
          title: "Tampons",
          content:
            "Change every 4–8 hours. Risk of Toxic Shock Syndrome (TSS) if left in too long.",
        },
        {
          title: "Menstrual Cups",
          content:
            "Reusable and eco-friendly. Must be sterilized before and after each cycle.",
        },
        {
          title: "Period Panties",
          content:
            "Reusable, absorbent underwear good for light to moderate flow days.",
        },
      ],
    },
    {
      id: "tss",
      title: "Toxic Shock Syndrome",
      icon: <FaHeartbeat className="text-[#f2a5e8] text-2xl" />,
      color: "bg-gradient-to-r from-[#f2a5e8] to-[#e8bffb]",
      tips: [
        {
          title: "What is TSS?",
          content: "Rare but serious condition linked to prolonged tampon use.",
        },
        {
          title: "Symptoms",
          content: "High fever, vomiting, rash, dizziness, muscle aches.",
        },
        {
          title: "Prevention",
          content:
            "Use lowest absorbency needed, change tampons often, consider alternatives like pads or cups.",
        },
      ],
      additional: (
        <div className="mt-6 bg-red-50 p-4 rounded-lg border border-red-100">
          <h3 className="font-bold text-red-600 mb-2 flex items-center">
            <FaMedkit className="text-red-400 mr-2" />
            Emergency Warning
          </h3>
          <p className="text-red-700">
            If you experience TSS symptoms, remove any tampon immediately and
            seek medical attention.
          </p>
        </div>
      ),
    },
    {
      id: "physical",
      title: "Physical Safety",
      icon: <MdHealthAndSafety className="text-[#bbbefe] text-2xl" />,
      color: "bg-gradient-to-r from-[#bbbefe] to-[#9ea3f5]",
      tips: [
        {
          title: "Avoid Overexertion",
          content:
            "Take it easy during heavy flow days - your body is working hard.",
        },
        {
          title: "Public Restrooms",
          content:
            "Plan ahead to ensure access to clean facilities when outside.",
        },
        {
          title: "Emergency Kit",
          content:
            "Always carry spare products, wipes, pain relief, and extra underwear.",
        },
      ],
    },
    {
      id: "mental",
      title: "Emotional Health",
      icon: <MdEmojiPeople className="text-[#d099e2] text-2xl" />,
      color: "bg-gradient-to-r from-[#d099e2] to-[#f2a5e8]",
      tips: [
        {
          title: "Mood Changes",
          content:
            "Hormonal fluctuations may cause irritability - be kind to yourself.",
        },
        {
          title: "Self-Care",
          content: "Rest, hydrate, try gentle yoga, or warm baths to relax.",
        },
        {
          title: "Support System",
          content: "Talk to friends/family about how you're feeling.",
        },
      ],
      additional: (
        <div className="mt-6 bg-[#f5e9ff] p-4 rounded-lg border border-[#e8d0ff]">
          <h3 className="font-bold text-[#6e5ba8] mb-2 flex items-center">
            <FaRegSadTear className="text-[#d099e2] mr-2" />
            Remember
          </h3>
          <p className="text-[#5a4d8a]">
            It's completely normal to feel emotional during your period. Your
            feelings are valid.
          </p>
        </div>
      ),
    },
    {
      id: "disposal",
      title: "Safe Disposal",
      icon: <FaTrashAlt className="text-[#ccdcfd] text-2xl" />,
      color: "bg-gradient-to-r from-[#ccdcfd] to-[#bbbefe]",
      tips: [
        {
          title: "Wrapping",
          content:
            "Always wrap used products in toilet paper or disposal bags.",
        },
        {
          title: "Don't Flush",
          content: "Pads and tampons can cause serious plumbing issues.",
        },
        {
          title: "Sanitary Bins",
          content: "Use designated disposal bins with lids when available.",
        },
      ],
    },
    {
      id: "warning",
      title: "When to See a Doctor",
      icon: <FaExclamationTriangle className="text-red-400 text-2xl" />,
      color: "bg-gradient-to-r from-red-300 to-red-200",
      tips: [
        {
          title: "Heavy Bleeding",
          content: "Soaking through a pad/tampon every hour for several hours.",
        },
        {
          title: "Severe Pain",
          content: "Cramps not relieved by OTC medication or heating pads.",
        },
        {
          title: "Irregularities",
          content:
            "Missed periods (without pregnancy) or cycles shorter than 21 days.",
        },
        {
          title: "Infections",
          content: "Foul odor, unusual discharge, or persistent itching.",
        },
      ],
    },
  ];

  return (
    <div className="w-full font-nunito min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-YR8YMW9ERY"
        ></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-YR8YMW9ERY', {
              page_path: window.location.pathname,
            });
          `}
        </script>
        <title>Period Safety Awareness - FlowHearts</title>
        <meta
          name="description"
          content="Explore FlowHearts' Period Safety Awareness guide for essential tips on menstrual hygiene, safe product use, and emotional well-being during menstruation."
        />
        <link rel="canonical" href="https://flowhearts.com/safety-awareness" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://flowhearts.com/safety-awareness" />
        <meta
          property="og:title"
          content="Period Safety Awareness - FlowHearts"
        />
        <meta
          property="og:description"
          content="Discover expert tips for safe menstruation with FlowHearts' Period Safety Awareness guide, covering hygiene practices, menstrual products, and health precautions."
        />
        <meta
          property="og:image"
          content="https://flowhearts.com/images/safety-awareness-preview.jpg"
        />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://flowhearts.com/safety-awareness" />
        <meta
          property="twitter:title"
          content="Period Safety Awareness - FlowHearts"
        />
        <meta
          property="twitter:description"
          content="Discover expert tips for safe menstruation with FlowHearts' Period Safety Awareness guide, covering hygiene practices, menstrual products, and health precautions."
        />
        <meta
          property="twitter:image"
          content="https://flowhearts.com/images/safety-awareness-preview.jpg"
        />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "FlowHearts Period Safety Awareness",
              "url": "https://flowhearts.com/safety-awareness",
              "description": "Learn essential tips for safe menstruation with FlowHearts' Period Safety Awareness, covering hygiene, menstrual products, and health precautions",
              "applicationCategory": "HealthApplication",
              "operatingSystem": "Web",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "creator": {
                "@type": "Organization",
                "name": "FlowHearts"
              }
            }
          `}
        </script>
      </Helmet>
      <div className="max-w-8xl mx-auto">
        <div className="flex justify-start mb-6">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors duration-200"
          >
            <FiHome className="text-pink-500" />
            <span className="text-purple-700 font-medium">Back to Home</span>
          </Link>
        </div>
        <div className="relative mb-8">
          <header className="text-center">
            <h2 className="text-3xl font-cormorant font-bold text-[#5a4d8a] mb-2">
              Period Safety Awareness
            </h2>
            <p className="text-lg text-[#6e5ba8]">
              Essential information for healthy and safe menstruation
            </p>
          </header>
        </div>
        <div className="space-y-6">
          {safetyData.map((section) => (
            <div
              key={section.id}
              className="rounded-xl overflow-hidden shadow-md"
            >
              <div className={`${section.color} p-4 text-white`}>
                <div className="flex items-center">
                  <div className="mr-4 bg-white p-3 rounded-full">
                    {React.cloneElement(section.icon, {
                      className: "text-[#5a4d8a] text-2xl",
                    })}
                  </div>
                  <h2 className="text-2xl font-bold">{section.title}</h2>
                </div>
              </div>
              <div className="bg-white p-6">
                <div className="space-y-4">
                  {section.tips.map((tip, index) => (
                    <div
                      key={index}
                      className="border-l-4 border-[#bbbefe] pl-4 py-2 bg-[#f9f7ff] rounded-r-lg"
                    >
                      <h3 className="font-semibold text-[#6e5ba8] flex items-center">
                        <FaHeart className="text-[#e8bffb] mr-2 text-sm" />
                        {tip.title}
                      </h3>
                      <p className="text-[#5a4d8a] mt-1 pl-5">{tip.content}</p>
                    </div>
                  ))}
                </div>
                {section.additional && section.additional}
              </div>
            </div>
          ))}
        </div>
        {/* Educational Section */}
        <div className="mt-12 bg-white rounded-3xl shadow-xl p-8">
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-[#5a4d8a] mb-6 flex items-center">
              <FaShower className="mr-3 text-[#9ea3f5]" />
              Getting Started with Menstrual Safety
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#f9f7ff] rounded-xl p-6 border border-[#bbbefe]">
                <div className="w-12 h-12 bg-[#9ea3f5] text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  1
                </div>
                <h3 className="text-lg font-semibold text-center text-[#6e5ba8] mb-2">
                  Choose Safe Products
                </h3>
                <p className="text-[#5a4d8a] text-center">
                  Select menstrual products suited to your flow and comfort
                </p>
              </div>
              <div className="bg-[#f9f7ff] rounded-xl p-6 border border-[#bbbefe]">
                <div className="w-12 h-12 bg-[#9ea3f5] text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  2
                </div>
                <h3 className="text-lg font-semibold text-center text-[#6e5ba8] mb-2">
                  Build Hygiene Habits
                </h3>
                <p className="text-[#5a4d8a] text-center">
                  Maintain regular hygiene routines for health
                </p>
              </div>
              <div className="bg-[#f9f7ff] rounded-xl p-6 border border-[#bbbefe]">
                <div className="w-12 h-12 bg-[#9ea3f5] text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  3
                </div>
                <h3 className="text-lg font-semibold text-center text-[#6e5ba8] mb-2">
                  Monitor Your Body
                </h3>
                <p className="text-[#5a4d8a] text-center">
                  Track symptoms and seek help if needed
                </p>
              </div>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-[#5a4d8a] mb-6 flex items-center">
              <MdHealthAndSafety className="mr-3 text-[#bbbefe]" />
              Science of Menstrual Safety
            </h2>
            <div className="bg-gradient-to-br from-[#f9f7ff] to-[#e8bffb] rounded-xl p-6 border border-[#bbbefe] mb-6">
              <h3 className="text-xl font-semibold text-[#5a4d8a] mb-4">
                Why Hygiene Matters
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-[#6e5ba8] mb-2">
                    Infection Prevention
                  </p>
                  <p className="text-[#5a4d8a]">
                    Proper hygiene reduces infection risks by up to 25%
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-[#6e5ba8] mb-2">
                    Hormonal Balance
                  </p>
                  <p className="text-[#5a4d8a]">
                    Safe practices support overall reproductive health
                  </p>
                </div>
              </div>
              <p className="text-[#5a4d8a]">
                Research highlights that consistent hygiene and safe product use improve menstrual health outcomes.
              </p>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-[#5a4d8a] mb-6 flex items-center">
              <FaHeartbeat className="mr-3 text-[#f2a5e8]" />
              Benefits of Safe Menstrual Practices
            </h2>
            <div className="bg-gradient-to-br from-[#ccdcfd] to-[#f9f7ff] rounded-xl p-6 border border-[#bbbefe]">
              <p className="text-[#5a4d8a] mb-4">
                Safe menstrual practices enhance comfort and well-being during your cycle.
              </p>
              <ul className="space-y-3 text-[#5a4d8a]">
                <li className="flex items-start">
                  <FaHeart className="text-[#e8bffb] mr-2 mt-0.5" />
                  <span>Reduced risk of infections</span>
                </li>
                <li className="flex items-start">
                  <FaHeart className="text-[#e8bffb] mr-2 mt-0.5" />
                  <span>Improved confidence and comfort</span>
                </li>
                <li className="flex items-start">
                  <FaHeart className="text-[#e8bffb] mr-2 mt-0.5" />
                  <span>Better emotional well-being</span>
                </li>
                <li className="flex items-start">
                  <FaHeart className="text-[#e8bffb] mr-2 mt-0.5" />
                  <span>Environmentally conscious choices</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-[#5a4d8a] mb-6 flex items-center">
              <FaExclamationTriangle className="mr-3 text-red-400" />
              Debunking Menstrual Myths
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-red-50 rounded-xl p-6 border border-red-100">
                <h3 className="text-lg font-semibold text-red-600 mb-3">
                  Myth: Flushing Products Is Fine
                </h3>
                <p className="text-[#5a4d8a]">
                  Reality: Flushing can damage plumbing and the environment
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-100">
                <h3 className="text-lg font-semibold text-red-600 mb-3">
                  Myth: Periods Should Always Hurt
                </h3>
                <p className="text-[#5a4d8a]">
                  Reality: Severe pain may indicate a health issue
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-100">
                <h3 className="text-lg font-semibold text-red-600 mb-3">
                  Myth: Scented Products Are Better
                </h3>
                <p className="text-[#5a4d8a]">
                  Reality: Scented products can disrupt vaginal health
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-100">
                <h3 className="text-lg font-semibold text-red-600 mb-3">
                  Myth: Cups Are Unsafe
                </h3>
                <p className="text-[#5a4d8a]">
                  Reality: Menstrual cups are safe with proper care
                </p>
              </div>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-[#5a4d8a] mb-6 flex items-center">
              <FaTint className="mr-3 text-[#e8bffb]" />
              Daily Menstrual Safety Tips
            </h2>
            <div className="bg-gradient-to-br from-[#f9f7ff] to-[#ccdcfd] rounded-xl p-6 border border-[#bbbefe]">
              <p className="text-[#5a4d8a] mb-4">
                Simple habits to ensure safe and comfortable menstruation.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-[#6e5ba8] mb-2">Track Cycle</h3>
                  <p className="text-[#5a4d8a] text-sm">
                    Use an app to monitor flow and symptoms
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-[#6e5ba8] mb-2">Hygiene Routine</h3>
                  <p className="text-[#5a4d8a] text-sm">
                    Change products regularly and clean gently
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-[#6e5ba8] mb-2">Emergency Kit</h3>
                  <p className="text-[#5a4d8a] text-sm">
                    Carry essentials for unexpected needs
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-[#6e5ba8] mb-2">Rest and Relax</h3>
                  <p className="text-[#5a4d8a] text-sm">
                    Prioritize self-care during heavy flow days
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-[#5a4d8a] mb-6 flex items-center">
              <FaMedkit className="mr-3 text-red-400" />
              When to Seek Medical Help
            </h2>
            <div className="bg-red-50 rounded-xl p-6 border border-red-100">
              <p className="text-[#5a4d8a] mb-4">
                Consult a healthcare provider if you experience these issues.
              </p>
              <ul className="space-y-3 text-[#5a4d8a]">
                <li className="flex items-start">
                  <FaExclamationTriangle className="text-red-400 mr-2 mt-0.5" />
                  <span>Persistent pain or heavy bleeding</span>
                </li>
                <li className="flex items-start">
                  <FaExclamationTriangle className="text-red-400 mr-2 mt-0.5" />
                  <span>Unusual discharge or odor</span>
                </li>
                <li className="flex items-start">
                  <FaExclamationTriangle className="text-red-400 mr-2 mt-0.5" />
                  <span>Irregular cycles or missed periods</span>
                </li>
                <li className="flex items-start">
                  <FaExclamationTriangle className="text-red-400 mr-2 mt-0.5" />
                  <span>Signs of Toxic Shock Syndrome</span>
                </li>
              </ul>
              <p className="text-[#5a4d8a] mt-4">
                Early intervention can prevent complications and ensure well-being.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-6 text-center text-sm text-[#6e5ba8]">
          <p>Every body is different - find what works best for you!</p>
        </div>
      </div>
    </div>
  );
};

export default SafetyAwareness;